import React, { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { InnerBox, OuterBox } from './two-columns'
import styles from '../component-styles/bulk-import.module.scss'
import Button from './button'
import {
  getBulkTemplateDownloadLink,
  uploadBulkTemplate,
} from '../api-client/track-client'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import ConfirmationButtons from './confirmation-buttons'
import { getCampaignCodesByAccountAsync } from '../store/actions'
import FileDragAndDrop from './file-drag-and-drop'

interface Props {
  onSuccess?: () => void
  setShowModal?: (status: boolean) => void
}

export default function BulkImport({
  onSuccess,
  setShowModal,
}: Props): React.ReactElement {
  const dispatch = useDispatch()
  const [error, setError] = useState('')
  const [importInProgress, setImportInProgress] = useState(false)
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setImportInProgress(true)
      const res = await uploadBulkTemplate({ file: acceptedFiles.pop() })
      if (res === true) {
        setError('')
        setImportInProgress(false)
        dispatch(getCampaignCodesByAccountAsync())
        if (onSuccess) {
          onSuccess()
        }
        return
      }

      const type = res?.error?.response?.data?.error || ''
      if (type === 'BAD_TEMPLATE') {
        setError(
          'Incorrect import template. Please download and use the sample template.',
        )
      } else if (type === 'BAD_FILE_FORMAT') {
        setError('Incorrect file type.')
      } else if (type === 'FAILED_UPLOAD') {
        setError('File upload has failed, please try again.')
      } else {
        setError(
          'Your file contains errors. Please email <a href="mailto:support@uplifter.ai">support@uplifter.ai</a> for help.',
        )
      }
      setImportInProgress(false)
    }
    // eslint-disable-next-line
  }, [])

  const content = () => {
    return (
      <>
        <p>
          This feature is designed to add previously created landing pages with
          campaign codes.
        </p>
        <p>
          To import and append codes into Uplifter, you need to upload a CSV
          with matching headers. You can download a template with your headings
          here.
        </p>
        <p className={styles.note}>
          <strong>Note:</strong> You cannot create short links for campaign
          links when using the bulk importer.
        </p>

        <Button
          href={getBulkTemplateDownloadLink()}
          download
          className={styles.templateButton}
        >
          Download template
        </Button>
        <FileDragAndDrop
          error={error}
          onDrop={onDrop}
          inProgress={importInProgress}
        />
      </>
    )
  }

  if (setShowModal) {
    return (
      <Modal
        active
        toggleActive={() => {
          setShowModal(false)
        }}
        type="white"
      >
        <ModalHead>
          <Heading type={3} align="left">
            Bulk import codes
          </Heading>
        </ModalHead>
        <ModalBody>{content()}</ModalBody>
        <ModalFooter>
          <ConfirmationButtons
            type="simple"
            disabled
            hideDisabled
            // onYes={(): void => {
            //   setShowModal(false)
            // }}
            onNo={(): void => {
              setShowModal(false)
            }}
            // yesLabel="Add new codes"
            noLabel="Go back"
          />
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <OuterBox className={styles.outerBox}>
      <InnerBox>
        <h2>Bulk import codes</h2>
        {content()}
      </InnerBox>
    </OuterBox>
  )
}
